import React, { useState, useMemo } from "react";
import { Chip, Table, TableBody, TableCell, TableContainer, TableRow, Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import clsx from "clsx";
import { format } from "date-fns";
import { blue, green, orange, red } from "@mui/material/colors";
import useSiteAudits from "../hooks/queries/useSiteAudits";
import CardBase from "../../components/cards/CardBase";
import { useAppSelector } from "@/redux/hooks";
import { tableSortingOrder } from "../../constants/tableConstants";
import EnhancedTableHead from "../../components/table/EnhancedTableHead";
import { getComparator, stableSort } from "../../utils/tableUtils";
import { getAuditTypeDescription, auditType } from "../../constants/auditConstants";
import { useSnackbar } from "notistack";
import { downloadFileFromBlob } from "../../utils/fileUtils";
import CustomButton from "../../components/CustomButton";
import useDownloadAuditPdf from "../hooks/queries/useDownloadAuditPdf";
import TablePagination from '@mui/material/TablePagination';

const useStyles = makeStyles(() => ({
    statusChip: {
        color: "white",
        borderRadius: "4px",
        "&.status-0": {
            backgroundColor: orange[400],
        },
        "&.status-1": {
            backgroundColor: red[400],
        },
        "&.status-2": {
            backgroundColor: green[400],
        },
        "&.status-3": {
            backgroundColor: blue[400],
        },
    },
    formControl: {
        minWidth: 180,
        '& .MuiInputLabel-outlined': {
            transform: 'translate(14px, 12px) scale(1)',
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75)',
        },
    },
}));

const headCells = [
    {
        id: 'id',
        label: 'Audit Id',
        isSortable: true,
    },
    {
        id: "type",
        label: "Audit Type"
    },
    {
        id: "siteExternalId",
        label: "Site",
        isSortable: true,
    },
    {
        id: "name",
        label: "Visit Name",
        isSortable: true,
    },
    {
        id: "startDate",
        label: "Audit Date",
        isSortable: true,
    },
    {
        id: "lastModifiedDate",
        label: "Last Modified Date",
        isSortable: true,
    },
    {
        id: "status",
        label: "Status",
        isSortable: true,
    },
    {
        id: "completedActionsSummary",
        label: "Completed Actions / Total Actions",
        isSortable: true,
    },
    {
        id: "consultant.fullName",
        label: "Auditor",
        isSortable: true,
    },
    {
        id: "Action",
    },
];

function AuditsTableCard() {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [order, setOrder] = useState(tableSortingOrder.asc);
    const [orderByColumn, setOrderByColumn] = useState(headCells[3].id);
    const [downloadInfo, setDownloadInfo] = useState(null);
    const [selectedAuditType, setSelectedAuditType] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    const downloadFile = useDownloadAuditPdf(
        { auditId: downloadInfo?.auditId, isDriAudit: downloadInfo?.isDriAudit },
        { onSuccess: onDownloadSuccess, onError: onDownloadError }
    );
    const { selectedSite, activeAppId, appSiteIds, sites } = useAppSelector(
        (state) => state.account
    );
    const { data: audits, isLoading, error, isFetching } = useSiteAudits(
        {
            externalIds: selectedSite
                ? [selectedSite.externalId]
                : appSiteIds[activeAppId]
        });

    const filteredAudits = useMemo(() => {
        let list = audits || [];

        if (selectedAuditType != null)
            list = audits.filter(x => x.type === selectedAuditType);

        return list;

    }, [audits, selectedAuditType]);

    const displayedAudits = useMemo(() => {
        let list = stableSort(filteredAudits, getComparator(order, orderByColumn))

        return list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    }, [filteredAudits, order, orderByColumn, page, rowsPerPage]);

    const auditTypeOptions = React.useMemo(() => {
        // Get all possible audit types from the constants
        const allAuditTypes = Object.values(auditType);

        
        // Get unique audit types from the actual data
        const existingAuditTypes = new Set((audits || []).map(x => x.type));
       
        const options = allAuditTypes
            .filter(type => existingAuditTypes.has(type))
            .map(type => ({
                value: type,
                label: getAuditTypeDescription(type)
            }))
            .filter(type => type.label)
            .sort((a, b) => a.label.localeCompare(b.label));

        return options;
    }, [audits]);


    const getDisplayLabel = React.useCallback((value) => {
        if (value === null || value === "") return "";
        return getAuditTypeDescription(value) || `Type ${value}`;
    }, []);

    function onDownloadSuccess(data) {
        if (!downloadInfo?.filename) {
            console.error(
                "Could not download audit document. File in state is missing!"
            );
            return;
        }

        downloadFileFromBlob(data, downloadInfo.filename);
        setDownloadInfo(null);
    }

    function onDownloadError(error) {
        console.error(error);
        enqueueSnackbar("Could not download audit document.", { variant: "error" });
        setDownloadInfo(null);
    }

    const downloadFileForAudit = (audit) => {
        setDownloadInfo({
            auditId: audit.id,
            filename: audit.documents[0].name,
            isDriAudit: audit.isDriAudit,
        });
    };

    const handleSortRequest = (event, property) => {
        const isAsc = orderByColumn === property && order === tableSortingOrder.asc;
        setOrder(isAsc ? tableSortingOrder.desc : tableSortingOrder.asc);
        setOrderByColumn(property);
    };

    const handleAuditTypeChange = (event) => {
        const newAuditType = event.target.value === "" ? null : Number(event.target.value);
        setSelectedAuditType(newAuditType);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const tableContent = () => {
        if (displayedAudits.length) {
            return displayedAudits
                .map((audit) => {
                    const statusChipClass = clsx(classes.statusChip, `status-${audit.status}`);

                    return (
                        <TableRow key={`${audit.id}-${audit.isDriAudit}`}>
                            <TableCell>
                                {audit.id}
                            </TableCell>
                            <TableCell component="th" scope="row">
                                {getAuditTypeDescription(audit.type)}
                            </TableCell>
                            <TableCell>{sites[audit.siteExternalId]?.name}</TableCell>
                            <TableCell>{audit.name || "-"}</TableCell>
                            <TableCell>
                                {format(new Date(audit.startDate), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>
                                {format(new Date(audit.lastModifiedDate), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    className={statusChipClass}
                                    label={audit.statusDescription}
                                />
                            </TableCell>
                            <TableCell>
                                {audit.completedActionsSummary}
                            </TableCell>
                            <TableCell>{audit.consultant?.fullName ?? "N/A"}</TableCell>
                            <TableCell align="right">
                                {audit.documents.length > 0 && (
                                    <CustomButton
                                        isLoading={
                                            downloadFile.isLoading &&
                                            downloadInfo?.auditId === audit.id &&
                                            downloadInfo?.isDriAudit === audit.isDriAudit
                                        }
                                        onClick={() => downloadFileForAudit(audit)}
                                        variant="text"
                                        size="small"
                                        color="primary"
                                    >
                                        View
                                    </CustomButton>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                });
        }

        return (
            <TableRow>
                <TableCell colSpan={headCells.length}>
                    <Typography>No audit data.</Typography>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <CardBase
            title="Audits"
            rightComponent={
                <FormControl 
                    variant="outlined" 
                    size="small" 
                    className={classes.formControl}
                >
                    <InputLabel id="audit-type-label">Audit Type</InputLabel>
                    <Select
                        labelId="audit-type-label"
                        id="audit-type-select"
                        value={selectedAuditType ?? ""}
                        onChange={handleAuditTypeChange}
                        label="Audit Type"
                        renderValue={(value) => value === "" ? <em>All</em> : getDisplayLabel(value)}
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        {auditTypeOptions.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            }
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
        >
            <TableContainer>
                <Table aria-label="audit-table">
                    <EnhancedTableHead
                        headCells={headCells}
                        sortDirection={order}
                        sortColumn={orderByColumn}
                        onRequestSort={handleSortRequest}
                    />
                    <TableBody>{tableContent()}</TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={filteredAudits.length || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
            />
        </CardBase>
    );
}

export default AuditsTableCard;